import {
  readJson,
  type Item,
  type ItemInput,
  type OwnedItem,
  type GiftList,
} from "../types"
import { csrfFetch } from "./csrf"
import { mutationRequest } from "./mutationRequest"

const BASE_URL = "/api/v1"

export async function fetchUserList<T extends OwnedItem = OwnedItem>(
  userId: string | number
) {
  const response = await fetch(`${BASE_URL}/users/${userId}/list`, {
    credentials: "include",
  })
  if (response.status === 404) return null
  if (!response.ok) throw new Error("Failed to fetch list")
  const { data } = await readJson<GiftList<T>>(response)
  return data
}

export async function createItem(listId: number, item: ItemInput) {
  const response = await csrfFetch(`${BASE_URL}/lists/${listId}/items`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ item }),
  })
  const json = await readJson<OwnedItem>(response)
  if (!response.ok)
    throw new Error(
      json.error || (json.errors || []).join(", ") || "Failed to add item"
    )
  return json.data
}

export async function updateItem(
  listId: number,
  itemId: number,
  item: ItemInput
) {
  const response = await csrfFetch(
    `${BASE_URL}/lists/${listId}/items/${itemId}`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ item }),
    }
  )
  const json = await readJson<OwnedItem>(response)
  if (!response.ok)
    throw new Error(
      json.error || (json.errors || []).join(", ") || "Failed to update item"
    )
  return json.data
}

export async function deleteItem(listId: number, itemId: number) {
  await mutationRequest(
    `${BASE_URL}/lists/${listId}/items/${itemId}`,
    {
      method: "DELETE",
      credentials: "include",
    },
    "Failed to delete item. Please try again."
  )
}

export async function purchaseItem(listId: number, itemId: number) {
  const response = await mutationRequest(
    `${BASE_URL}/lists/${listId}/items/${itemId}/purchase`,
    {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    },
    "Failed to mark as purchased. Please try again."
  )
  const json = await readJson<Item>(response)
  return json.data
}
